import { z } from "zod";

export const appStatusSchema = z.enum(["active", "disabled"]);
export type AppStatus = z.infer<typeof appStatusSchema>;

export const payChannelSchema = z.enum(["wxpay", "alipay", "apple"]);
export type PayChannel = z.infer<typeof payChannelSchema>;

export const appSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(2).max(64),
  code: z.string().trim().regex(/^[a-z][a-z0-9-]{2,31}$/),
  description: z.string().trim().max(300),
  status: appStatusSchema,
  platforms: z.array(z.enum(["web", "mini-program", "android", "ios"])).default(["web"]),
  payChannels: z.array(payChannelSchema),
  configCount: z.number().int().nonnegative(),
  updatedAt: z.string().datetime(),
});
export type AppRecord = z.infer<typeof appSchema>;

export const createAppInputSchema = appSchema.pick({
  name: true,
  code: true,
  description: true,
  platforms: true,
});
export type CreateAppInput = z.input<typeof createAppInputSchema>;

export const updateAppInputSchema = z.object({
  expectedUpdatedAt: z.string().datetime(),
  name: appSchema.shape.name.optional(),
  code: appSchema.shape.code.optional(),
  description: appSchema.shape.description.optional(),
  status: appStatusSchema.optional(),
  platforms: appSchema.shape.platforms.removeDefault().min(1).optional(),
}).refine((input) => Object.keys(input).some((key) => key !== "expectedUpdatedAt"), { message: "至少提交一个需要更新的字段" });
export type UpdateAppInput = z.infer<typeof updateAppInputSchema>;

export const appListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  status: z.union([appStatusSchema, z.literal("all")]).default("all"),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type AppListQuery = z.infer<typeof appListQuerySchema>;

export const credentialRevealSchema = z.object({
  appKey: z.string().min(1),
  appSecret: z.string().min(32),
  revealedAt: z.string().datetime(),
});
export type CredentialReveal = z.infer<typeof credentialRevealSchema>;

export const credentialMetadataSchema = z.object({
  appKey: z.string().min(1),
  secretLastResetAt: z.string().datetime(),
});
export type CredentialMetadata = z.infer<typeof credentialMetadataSchema>;

export const appConfigSchema = z.object({
  key: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/),
  value: z.string().max(4000),
  valueType: z.enum(["string", "number", "boolean", "json"]),
  isPublic: z.boolean(),
  description: z.string().max(200),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

export const appConfigInputSchema = appConfigSchema.omit({ key: true });
export type AppConfigInput = z.infer<typeof appConfigInputSchema>;

export const payChannelConfigSchema = z.object({
  channel: payChannelSchema,
  enabled: z.boolean(),
  accountId: z.string().trim().min(1).max(128),
  secretRef: z.string().trim().regex(/^[A-Z][A-Z0-9_]{2,127}$/),
  description: z.string().trim().max(200),
  updatedAt: z.string().datetime(),
});
export type PayChannelConfig = z.infer<typeof payChannelConfigSchema>;

export const upsertPayChannelInputSchema = payChannelConfigSchema.omit({
  updatedAt: true,
});
export type UpsertPayChannelInput = z.infer<typeof upsertPayChannelInputSchema>;

export const openTokenBodySchema = z.object({}).strict();

export const openTokenResultSchema = z.object({
  accessToken: z.string().min(32),
  tokenType: z.literal("Bearer"),
  expiresIn: z.literal(7200),
  expiresAt: z.string().datetime(),
});
export type OpenTokenResult = z.infer<typeof openTokenResultSchema>;

export const publicConfigResultSchema = z.object({
  appKey: z.string().min(1),
  configs: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.json()])),
});
export type PublicConfigResult = z.infer<typeof publicConfigResultSchema>;

const drivingProfileOptionSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,32}$/),
  label: z.string().trim().min(1).max(40),
  labelUg: z.string().trim().min(1).max(80),
}).strict();
const drivingProfileFieldSchema = z.object({
  label: z.string().trim().min(1).max(40),
  labelUg: z.string().trim().min(1).max(80),
  options: z.array(drivingProfileOptionSchema).min(1).max(20),
}).strict();
export const drivingProfileOptionsSchema = z.object({
  occupation: drivingProfileFieldSchema,
  licenseType: drivingProfileFieldSchema,
  examStage: drivingProfileFieldSchema,
  interests: drivingProfileFieldSchema,
  maxInterests: z.number().int().min(1).max(3).default(3),
}).strict();
export type DrivingProfileOptions = z.infer<typeof drivingProfileOptionsSchema>;

export const applicationUserIdentityTypeSchema = z.enum(["phone", "email", "union_id"]);
export type ApplicationUserIdentityType = z.infer<typeof applicationUserIdentityTypeSchema>;

export const applicationUserLoginInputSchema = z.object({
  identityType: applicationUserIdentityTypeSchema,
  identity: z.string().trim().min(3).max(320),
  displayName: z.string().trim().max(64).default(""),
  avatarUrl: z.string().url().max(512).nullable().optional(),
  profile: z.record(z.string(), z.unknown()).default({}),
  device: z.string().trim().max(128).default(""),
  ipMasked: z.string().trim().max(64).default(""),
  location: z.string().trim().max(128).default(""),
  region: z.string().trim().max(64).default(""),
  locale: z.string().trim().max(16).default(""),
  gender: z.enum(["male", "female", "unknown"]).default("unknown"),
  ageGroup: z.enum(["18-24", "25-34", "35-44", "45+", "unknown"]).default("unknown"),
  deviceType: z.enum(["ios", "android", "desktop", "other"]).default("other"),
  consent: z.boolean().default(false),
}).strict();
export type ApplicationUserLoginInput = z.infer<typeof applicationUserLoginInputSchema>;

/** Untrusted mini-program input: identities are obtained only from WeChat. */
export const wechatUserLoginInputSchema = z.object({
  code: z.string().min(1).max(256).regex(/^[A-Za-z0-9_-]+$/),
  consent: z.boolean().default(false),
  device: z.string().trim().max(128).optional(),
  deviceType: z.enum(["ios", "android", "desktop", "other"]).optional(),
  ipMasked: z.string().trim().max(64).optional(),
  region: z.string().trim().max(64).optional(),
}).strict();
export type WechatUserLoginInput = z.infer<typeof wechatUserLoginInputSchema>;
export const wechatSecretReferenceSchema = z.string().regex(/^(?:env:\/\/[A-Z][A-Z0-9_]{2,100}|stored:\/\/wechat\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/);
export const wechatLoginConfigSchema = z.object({
  wxAppId: z.string().regex(/^wx[a-fA-F0-9]{16}$/),
  appSecretRef: wechatSecretReferenceSchema,
  enabled: z.boolean(),
  maxAttemptsPerMinute: z.number().int().min(1).max(10000),
}).strict();
export type WechatLoginConfig = z.infer<typeof wechatLoginConfigSchema>;

export const wechatLoginSettingsSchema = z.object({
  appId: z.string().min(1),
  wxAppId: wechatLoginConfigSchema.shape.wxAppId.nullable(),
  enabled: z.boolean(),
  maxAttemptsPerMinute: wechatLoginConfigSchema.shape.maxAttemptsPerMinute,
  secretSource: z.enum(["none", "environment", "stored"]),
  secretConfigured: z.boolean(),
  appIdLocked: z.boolean(),
  version: z.string().regex(/^[a-f0-9]{32}$/).nullable(),
  updatedAt: z.string().datetime().nullable(),
}).strict();
export type WechatLoginSettings = z.infer<typeof wechatLoginSettingsSchema>;
export const upsertWechatLoginSettingsSchema = z.object({
  wxAppId: wechatLoginConfigSchema.shape.wxAppId,
  appSecret: z.string().trim().regex(/^[A-Za-z0-9]{32}$/, "AppSecret 应为微信提供的 32 位字母数字密钥").optional(),
  enabled: z.boolean(),
  maxAttemptsPerMinute: wechatLoginConfigSchema.shape.maxAttemptsPerMinute,
  expectedVersion: wechatLoginSettingsSchema.shape.version,
}).strict();
export type UpsertWechatLoginSettings = z.infer<typeof upsertWechatLoginSettingsSchema>;

export const phoneAuthenticationChallengeSchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{7,14}$/),
  templateCode: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
  mutationId: z.uuid(),
}).strict();
export const phoneAuthenticationVerifySchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{7,14}$/),
  challengeId: z.uuid(),
  code: z.string().regex(/^\d{6}$/),
  consent: z.boolean().default(false),
}).strict();
export const phoneUserLoginInputSchema = phoneAuthenticationVerifySchema.extend({
  wechatCode: wechatUserLoginInputSchema.shape.code.optional(),
}).strict();
export const phoneBindResultSchema = z.object({ bound: z.literal(true), userId: z.uuid(), maskedPhone: z.string() }).strict();
export const wechatPhoneBindInputSchema = z.object({
  code: wechatUserLoginInputSchema.shape.code,
  consent: z.literal(true),
}).strict();

export const applicationUserSchema = z.object({
  userId: z.string().min(1),
  publicId: z.number().int().min(1001),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  profile: z.record(z.string(), z.unknown()),
  appId: z.string().min(1),
  appName: z.string(),
  loginCount: z.number().int().nonnegative(),
  lastLoginAt: z.string().datetime(),
});
export type ApplicationUser = z.infer<typeof applicationUserSchema>;

export const applicationUserSessionResultSchema = z.object({
  user: applicationUserSchema,
  sessionToken: z.string().min(32),
  expiresAt: z.string().datetime(),
});
export type ApplicationUserSessionResult = z.infer<typeof applicationUserSessionResultSchema>;

export const applicationUserSessionSchema = z.object({
  user: applicationUserSchema,
  expiresAt: z.string().datetime(),
});
export type ApplicationUserSession = z.infer<typeof applicationUserSessionSchema>;

// This namespace is user-editable, application-scoped data, never authorization data.
const selfProfileJsonSchema = z.unknown().superRefine((value, ctx) => {
  const visit = (node: unknown, depth: number): boolean => {
    if (depth > 6) return false;
    if (node && typeof node === "object") return Object.entries(node).every(([key, child]) => !["__proto__", "prototype", "constructor"].includes(key) && visit(child, depth + 1));
    return true;
  };
  if (!value || typeof value !== "object" || Array.isArray(value) || !visit(value, 0) || Object.keys(value).length > 100 || new TextEncoder().encode(JSON.stringify(value)).length > 16_384) {
    ctx.addIssue({ code: "custom", message: "扩展资料最多 100 个字段、16 KB，嵌套深度不得超过 6 层" });
  }
}).pipe(z.record(z.string().trim().min(1).max(64).refine((key) => !["__proto__", "prototype", "constructor"].includes(key), "扩展资料字段名不合法"), z.json()));
export const userMeSchema = z.object({
  userId: z.string(), publicId: z.number().int(), appId: z.string(),
  displayName: z.string(), avatarUrl: z.string().nullable(),
  gender: z.enum(["male", "female", "unknown"]), region: z.string(), locale: z.string(),
  version: z.number().int().positive(), appProfileVersion: z.number().int().positive(),
  appProfile: z.record(z.string(), z.json()),
});
export const updateUserMeSchema = z.object({
  version: z.number().int().positive().max(2147483646),
  appProfileVersion: z.number().int().positive().max(2147483646).optional(),
  displayName: z.string().trim().min(1).max(64).optional(),
  gender: z.enum(["male", "female", "unknown"]).optional(),
  region: z.string().trim().max(64).optional(), locale: z.string().trim().max(16).optional(),
  appProfile: selfProfileJsonSchema.optional(),
}).strict().superRefine((value, ctx) => {
  if (!["displayName", "gender", "region", "locale", "appProfile"].some(key => key in value)) ctx.addIssue({ code: "custom", message: "至少提交一个待修改字段" });
  if (value.appProfile !== undefined && value.appProfileVersion === undefined) ctx.addIssue({ code: "custom", path: ["appProfileVersion"], message: "修改应用资料需要版本号" });
});
export type UserMe = z.infer<typeof userMeSchema>;
export const userSecuritySchema = z.object({ phoneBound: z.boolean(), maskedPhone: z.string().nullable() });
export type UserSecurity = z.infer<typeof userSecuritySchema>;
export type UpdateUserMe = z.infer<typeof updateUserMeSchema>;

export const createAppResultSchema = z.object({
  app: appSchema,
  credential: credentialRevealSchema,
});
export type CreateAppResult = z.infer<typeof createAppResultSchema>;

export const apiSuccessSchema = <T extends z.ZodType>(data: T) =>
  z.object({
    success: z.literal(true),
    data,
    request_id: z.string().min(1),
  });

export const apiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    fields: z.record(z.string(), z.string()).optional(),
  }),
  request_id: z.string().min(1),
});
